import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, Save, Key, Eye, EyeOff, CheckCircle, AlertCircle, 
  Wallet, CreditCard, History, Settings, Shield, Trash2, Bot, TrendingUp,
  Copy, RefreshCw, Lock
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EXCHANGES = [
  { id: 'bitfinex', name: 'Bitfinex', logo: '🔵', status: 'available' },
  { id: 'binance', name: 'Binance (Global)', logo: '🟡', status: 'geo_restricted', note: 'May be blocked in some regions' },
  { id: 'binance_us', name: 'Binance.US', logo: '🟡', status: 'available', note: 'For US customers' },
  { id: 'kraken', name: 'Kraken', logo: '🟣', status: 'available' },
  { id: 'bybit', name: 'ByBit', logo: '🟠', status: 'geo_restricted' },
  { id: 'okx', name: 'OKX', logo: '⚫', status: 'available' },
  { id: 'gateio', name: 'Gate.io', logo: '🔷', status: 'available' },
  { id: 'crypto_com', name: 'Crypto.com', logo: '🔹', status: 'available' },
  { id: 'coinbase', name: 'Coinbase', logo: '🔵', status: 'available' },
  { id: 'revolutx', name: 'Revolut X', logo: '🟣', status: 'available' }
];

// Popular cryptocurrency trading pairs
const POPULAR_SYMBOLS = [
  { value: 'tBTCUSD', label: 'Bitcoin (BTC/USD)', name: 'Bitcoin' },
  { value: 'tETHUSD', label: 'Ethereum (ETH/USD)', name: 'Ethereum' },
  { value: 'tLTCUSD', label: 'Litecoin (LTC/USD)', name: 'Litecoin' },
  { value: 'tXRPUSD', label: 'Ripple (XRP/USD)', name: 'Ripple' },
  { value: 'tEOSUSD', label: 'EOS (EOS/USD)', name: 'EOS' },
  { value: 'tDOGE:USD', label: 'Dogecoin (DOGE/USD)', name: 'Dogecoin' },
  { value: 'tSHIB:USD', label: 'Shiba Inu (SHIB/USD)', name: 'Shiba Inu' },
  { value: 'tSOL:USD', label: 'Solana (SOL/USD)', name: 'Solana' },
  { value: 'tMATIC:USD', label: 'Polygon (MATIC/USD)', name: 'Polygon' },
  { value: 'custom', label: 'Custom Symbol...', name: 'Custom' }
];

// Strategy risk level badges
const STRATEGY_RISK = {
  'mean_reversion_rsi': { level: 'Medium', color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
  'trend_following': { level: 'Low', color: 'bg-green-500/20 text-green-300 border-green-500/30' },
  'breakout': { level: 'High', color: 'bg-red-500/20 text-red-300 border-red-500/30' },
  'grid_trading': { level: 'Low-Med', color: 'bg-blue-500/20 text-blue-300 border-blue-500/30' }
};

// Dynamic Strategy Parameters Component
const StrategyParameters = ({ strategy, strategies, settings, onChange }) => {
  const strategyConfig = strategies.find(s => s.name === strategy);
  const params = strategyConfig?.parameters || [];
  const risk = STRATEGY_RISK[strategy] || { level: 'Unknown', color: 'bg-gray-500/20 text-gray-300' };

  // Group parameters by type for better layout
  const numberParams = params.filter(p => p.type === 'int' || p.type === 'float');
  const boolParams = params.filter(p => p.type === 'bool');

  const renderInput = (param) => {
    const value = settings[param.name] !== undefined ? settings[param.name] : param.default;
    
    if (param.type === 'bool') {
      return (
        <div key={param.name} className="flex items-center justify-between p-4 rounded-lg bg-white/5">
          <div>
            <Label className="text-blue-100 font-semibold">{formatParamName(param.name)}</Label>
            <p className="text-sm text-blue-200 mt-1">{param.description}</p>
          </div>
          <Switch
            checked={value}
            onCheckedChange={(checked) => onChange(param.name, checked)}
            data-testid={`param-${param.name}`}
          />
        </div>
      );
    }

    return (
      <div key={param.name}>
        <Label className="text-blue-100">{formatParamName(param.name)}</Label>
        <Input
          type="number"
          step={param.type === 'float' ? '0.1' : '1'}
          min={param.min}
          max={param.max}
          value={value}
          onChange={(e) => {
            const newValue = param.type === 'int' 
              ? parseInt(e.target.value) 
              : parseFloat(e.target.value);
            onChange(param.name, newValue);
          }}
          className="mt-2 bg-white/5 border-white/20 text-white"
          data-testid={`param-${param.name}`}
        />
        <p className="text-xs text-blue-300 mt-1">{param.description}</p>
      </div>
    );
  };

  const formatParamName = (name) => {
    return name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase())
      .replace('Pct', '%')
      .replace('Ema', 'EMA')
      .replace('Adx', 'ADX')
      .replace('Rsi', 'RSI')
      .replace('Bb', 'BB')
      .replace('Atr', 'ATR');
  };

  if (params.length === 0) {
    return (
      <div className="text-center py-4 text-blue-200">
        No configurable parameters for this strategy
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Strategy Risk Badge */}
      <div className="flex items-center gap-2">
        <Badge className={`${risk.color} border`}>Risk: {risk.level}</Badge>
        {strategy === 'trend_following' && (
          <Badge className="bg-purple-500/20 text-purple-300 border border-purple-500/30">Best for Bull Markets</Badge>
        )}
        {strategy === 'grid_trading' && (
          <Badge className="bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">Best for Sideways</Badge>
        )}
        {strategy === 'breakout' && (
          <Badge className="bg-orange-500/20 text-orange-300 border border-orange-500/30">High Reward</Badge>
        )}
      </div>

      {/* Number Parameters Grid */}
      {numberParams.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {numberParams.map(param => renderInput(param))}
        </div>
      )}

      {/* Boolean Parameters */}
      {boolParams.length > 0 && (
        <div className="space-y-3">
          {boolParams.map(param => renderInput(param))}
        </div>
      )}
    </div>
  );
};

const AccountPage = () => {
  const { user, wallet, refreshWallet, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('bot');
  const [exchangeConfigs, setExchangeConfigs] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});

  // Form state for adding exchange
  const [selectedExchange, setSelectedExchange] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');

  // Bot settings state
  const [botSettings, setBotSettings] = useState({
    // Exchange & general
    exchange: 'bitfinex',
    strategy: 'mean_reversion_rsi',
    paper_trading: true,
    symbol: 'tBTCUSD',
    
    // Mean Reversion RSI params
    rsi_period: 14,
    rsi_oversold: 30,
    rsi_overbought: 70,
    
    // Trend Following params
    fast_ema_period: 50,
    slow_ema_period: 200,
    adx_period: 14,
    adx_threshold: 25,
    
    // Breakout params
    donchian_period: 20,
    bb_period: 20,
    squeeze_threshold: 0.5,
    volume_spike_multiplier: 1.5,
    min_breakout_pct: 0.5,
    
    // Grid Trading params
    grid_levels: 10,
    grid_spacing_pct: 1.0,
    adaptive_grid: true,
    trend_filter_enabled: true,
    max_trend_strength_pct: 3.0,
    position_size_per_level: 10,
    
    // Common risk management
    position_size: 25,
    stop_loss_pct: 2,
    take_profit_pct: 4,
    daily_loss_limit: 100,
    max_position_size: 25,
    poll_interval: 10,
    paper_balance_usd: 1000,
    enable_auto_short: false,
    min_profit_to_close_pct: 0
  });
  const [customSymbol, setCustomSymbol] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [availableExchanges, setAvailableExchanges] = useState([]);
  const [availableStrategies, setAvailableStrategies] = useState([]);
  
  // Revolut X Key Generator State
  const [showKeyGenerator, setShowKeyGenerator] = useState(false);
  const [generatedKeys, setGeneratedKeys] = useState(null);
  const [generatingKeys, setGeneratingKeys] = useState(false);
  
  // Platform config state
  const [creditValueUsd, setCreditValueUsd] = useState(0.01);

  useEffect(() => {
    fetchExchangeConfigs();
    fetchTransactions();
    fetchBotSettings();
    fetchExchangesAndStrategies();
    fetchPaymentConfig();
  }, []);

  const fetchPaymentConfig = async () => {
    try {
      const response = await axios.get(`${API}/payments/config`);
      if (response.data.credit_value_usd) {
        setCreditValueUsd(response.data.credit_value_usd);
      }
    } catch (error) {
      console.error('Failed to fetch payment config:', error);
    }
  };

  const fetchExchangesAndStrategies = async () => {
    try {
      const [exchangesRes, strategiesRes] = await Promise.all([
        axios.get(`${API}/exchanges`),
        axios.get(`${API}/strategies`)
      ]);
      setAvailableExchanges(exchangesRes.data.exchanges || []);
      setAvailableStrategies(strategiesRes.data.strategies || []);
    } catch (error) {
      console.error('Failed to fetch exchanges/strategies:', error);
    }
  };

  const fetchExchangeConfigs = async () => {
    try {
      const response = await axios.get(`${API}/user/exchange-configs`);
      setExchangeConfigs(response.data.configs || []);
    } catch (error) {
      console.error('Failed to fetch exchange configs:', error);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/user/transactions?limit=20`);
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    }
  };

  const fetchBotSettings = async () => {
    try {
      const response = await axios.get(`${API}/bot/settings`);
      if (response.data.data) {
        setBotSettings(prev => ({ ...prev, ...response.data.data }));
        // Check if symbol is custom
        const isPopular = POPULAR_SYMBOLS.some(s => s.value === response.data.data.symbol);
        setCustomSymbol(!isPopular && response.data.data.symbol !== 'custom');
      }
    } catch (error) {
      console.error('Failed to fetch bot settings:', error);
    }
  };

  const handleSaveExchange = async () => {
    if (!selectedExchange || !apiKey || !apiSecret) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const exchangeName = EXCHANGES.find(e => e.id === selectedExchange)?.name || selectedExchange;
      await axios.post(`${API}/user/exchange-configs?exchange=${selectedExchange}&api_key=${encodeURIComponent(apiKey)}&api_secret=${encodeURIComponent(apiSecret)}`);
      toast.success(`${exchangeName} API keys saved successfully! Check "Your Exchanges" below.`);
      setSelectedExchange('');
      setApiKey('');
      setApiSecret('');
      await fetchExchangeConfigs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExchange = async (exchange) => {
    if (!confirm(`Are you sure you want to delete ${exchange} configuration?`)) return;

    try {
      await axios.delete(`${API}/user/exchange-configs/${exchange}`);
      toast.success(`${exchange} configuration deleted`);
      fetchExchangeConfigs();
    } catch (error) {
      toast.error('Failed to delete configuration');
    }
  };

  const handleSaveBotSettings = async () => {
    setLoading(true);
    setSettingsSaved(false);
    try {
      const settingsToSave = { ...botSettings };
      
      // Symbol is already in the correct format for the selected exchange
      // No need to modify it - each exchange has its own symbol format
      
      const response = await axios.post(`${API}/bot/settings`, settingsToSave);
      
      if (response.data.bot_restarted) {
        toast.success('Settings saved - Bot restarted with new settings');
      } else {
        toast.success('Bot settings saved successfully');
      }
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleBotSettingChange = (field, value) => {
    setBotSettings(prev => ({ ...prev, [field]: value }));
  };

  // Ed25519 Key Pair Generator for Revolut X
  const generateEd25519KeyPair = useCallback(async () => {
    setGeneratingKeys(true);
    try {
      // Generate Ed25519 key pair using Web Crypto API
      const keyPair = await window.crypto.subtle.generateKey(
        { name: 'Ed25519' },
        true, // extractable
        ['sign', 'verify']
      );

      // Export private key in PKCS8 format
      const privateKeyBuffer = await window.crypto.subtle.exportKey('pkcs8', keyPair.privateKey);
      const privateKeyBase64 = btoa(String.fromCharCode(...new Uint8Array(privateKeyBuffer)));
      const privateKeyPEM = `-----BEGIN PRIVATE KEY-----\n${privateKeyBase64.match(/.{1,64}/g).join('\n')}\n-----END PRIVATE KEY-----`;

      // Export public key in SPKI format
      const publicKeyBuffer = await window.crypto.subtle.exportKey('spki', keyPair.publicKey);
      const publicKeyBase64 = btoa(String.fromCharCode(...new Uint8Array(publicKeyBuffer)));
      const publicKeyPEM = `-----BEGIN PUBLIC KEY-----\n${publicKeyBase64.match(/.{1,64}/g).join('\n')}\n-----END PUBLIC KEY-----`;

      setGeneratedKeys({
        privateKey: privateKeyPEM,
        publicKey: publicKeyPEM
      });

      toast.success('Ed25519 key pair generated successfully!');
    } catch (error) {
      console.error('Failed to generate keys:', error);
      // Fallback message if Ed25519 not supported
      if (error.name === 'NotSupportedError') {
        toast.error('Your browser does not support Ed25519. Please use Chrome 113+, Firefox 132+, or Safari 17+');
      } else {
        toast.error('Failed to generate keys: ' + error.message);
      }
    } finally {
      setGeneratingKeys(false);
    }
  }, []);

  const copyToClipboard = useCallback(async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard!`);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  }, []);

  const useGeneratedPrivateKey = useCallback(() => {
    if (generatedKeys?.privateKey) {
      setApiSecret(generatedKeys.privateKey);
      toast.success('Private key applied to API Secret field');
    }
  }, [generatedKeys]);

  const handleExchangeChange = (exchangeId) => {
    setBotSettings(prev => ({ ...prev, exchange: exchangeId }));
    
    // Auto-select first symbol for the new exchange
    const exchange = availableExchanges.find(e => e.id === exchangeId);
    if (exchange && exchange.popular_symbols && exchange.popular_symbols.length > 0) {
      setBotSettings(prev => ({ ...prev, symbol: exchange.popular_symbols[0].symbol }));
      setCustomSymbol(false);
    }
  };

  const handleSymbolChange = (value) => {
    if (value === 'custom') {
      setCustomSymbol(true);
      setBotSettings(prev => ({ ...prev, symbol: '' }));
    } else {
      setCustomSymbol(false);
      setBotSettings(prev => ({ ...prev, symbol: value }));
    }
  };

  // Get symbols for current exchange
  const getExchangeSymbols = () => {
    const exchange = availableExchanges.find(e => e.id === botSettings.exchange);
    if (!exchange) return POPULAR_SYMBOLS;
    
    return [
      ...exchange.popular_symbols.map(s => ({
        value: s.symbol,
        label: s.name,
        name: s.name.split('/')[0]
      })),
      { value: 'custom', label: 'Custom Symbol...', name: 'Custom' }
    ];
  };

  const getConfiguredExchange = (exchangeId) => {
    return exchangeConfigs.find(c => c.exchange === exchangeId);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'signup_bonus':
      case 'purchase':
        return 'text-green-400';
      case 'trade_fee':
        return 'text-red-400';
      case 'refund':
        return 'text-blue-400';
      default:
        return 'text-gray-400';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Dashboard
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-white">My Account</h1>
              <p className="text-blue-200">{user?.email}</p>
            </div>
          </div>
          <Button variant="outline" onClick={logout} className="bg-red-500/20 border-red-500/50 text-red-200 hover:bg-red-500/30">
            Logout
          </Button>
        </div>

        {/* Wallet Summary Card */}
        <Card className="mb-6 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-200 text-sm">Available Balance</p>
                <p className="text-4xl font-bold text-white">
                  {wallet?.balance_credits?.toFixed(0) || 0} <span className="text-lg text-green-300">credits</span>
                </p>
                <p className="text-green-200 text-sm mt-1">≈ ${wallet?.balance_usd?.toFixed(2) || '0.00'} USD</p>
              </div>
              <div className="text-right space-y-2">
                <Link to="/buy-credits">
                  <Button className="bg-green-500 hover:bg-green-600 text-white" data-testid="top-up-btn">
                    <CreditCard className="mr-2 h-4 w-4" />
                    Top Up Credits
                  </Button>
                </Link>
                <Link to="/referrals" className="block">
                  <Button variant="outline" className="w-full bg-white/10 border-white/20 text-white hover:bg-white/20" data-testid="referrals-btn">
                    <TrendingUp className="mr-2 h-4 w-4" />
                    Earn Free Credits
                  </Button>
                </Link>
                <p className="text-green-200 text-xs">1 credit = ${parseFloat(creditValueUsd.toFixed(3))}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white/10 border-white/20">
            <TabsTrigger value="bot" className="data-[state=active]:bg-white/20 text-white" data-testid="bot-settings-tab">
              <Bot className="mr-2 h-4 w-4" />
              Bot Settings
            </TabsTrigger>
            <TabsTrigger value="exchanges" className="data-[state=active]:bg-white/20 text-white" data-testid="exchanges-tab">
              <Key className="mr-2 h-4 w-4" />
              Exchange APIs
            </TabsTrigger>
            <TabsTrigger value="transactions" className="data-[state=active]:bg-white/20 text-white" data-testid="transactions-tab">
              <History className="mr-2 h-4 w-4" />
              Transactions
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-white/20 text-white" data-testid="settings-tab">
              <Settings className="mr-2 h-4 w-4" />
              Account
            </TabsTrigger>
          </TabsList>

          {/* Bot Settings Tab */}
          <TabsContent value="bot" className="space-y-6">
            {settingsSaved && (
              <Alert className="bg-green-500/20 border-green-500/50 text-green-100">
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>Bot settings saved successfully!</AlertDescription>
              </Alert>
            )}

            {/* Exchange & Strategy Selection */}
            <Card className="bg-white/10 border-white/20 backdrop-blur-sm" data-testid="exchange-strategy-card">
              <CardHeader>
                <CardTitle className="text-white">Exchange & Strategy</CardTitle>
                <CardDescription className="text-blue-200">Select where and how to trade</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Exchange Selection */}
                  <div>
                    <Label className="text-blue-100 font-semibold">Exchange</Label>
                    <Select value={botSettings.exchange} onValueChange={handleExchangeChange}>
                      <SelectTrigger className="mt-2 bg-white/5 border-white/20 text-white" data-testid="exchange-select">
                        <SelectValue placeholder="Select exchange" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-white/20">
                        {availableExchanges.map(ex => (
                          <SelectItem key={ex.id} value={ex.id} className="text-white hover:bg-white/10">
                            {ex.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-blue-300 mt-1">
                      {botSettings.exchange === 'binance' || botSettings.exchange === 'bybit' 
                        ? '✓ Testnet available' 
                        : 'Paper trading mode recommended'}
                    </p>
                  </div>

                  {/* Strategy Selection */}
                  <div>
                    <Label className="text-blue-100 font-semibold">Trading Strategy</Label>
                    <Select 
                      value={botSettings.strategy} 
                      onValueChange={(v) => handleBotSettingChange('strategy', v)}
                    >
                      <SelectTrigger className="mt-2 bg-white/5 border-white/20 text-white" data-testid="strategy-select">
                        <SelectValue placeholder="Select strategy" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-white/20">
                        {availableStrategies.map(strat => (
                          <SelectItem key={strat.name} value={strat.name} className="text-white hover:bg-white/10">
                            {strat.description || strat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-blue-300 mt-1">More strategies coming soon</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Trading Mode */}
            <Card className="bg-white/10 border-white/20 backdrop-blur-sm" data-testid="trading-mode-card">
              <CardHeader>
                <CardTitle className="text-white">Trading Mode</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                  <div>
                    <Label className="text-blue-100 font-semibold">Paper Trading Mode</Label>
                    <p className="text-sm text-blue-200 mt-1">Test without risking real money</p>
                  </div>
                  <Switch
                    checked={botSettings.paper_trading}
                    onCheckedChange={(checked) => handleBotSettingChange('paper_trading', checked)}
                    data-testid="paper-trading-switch"
                  />
                </div>
                {botSettings.paper_trading && (
                  <div className="mt-4">
                    <Label className="text-blue-100">Paper Trading Balance (USD)</Label>
                    <Input
                      type="number"
                      value={botSettings.paper_balance_usd}
                      onChange={(e) => handleBotSettingChange('paper_balance_usd', parseFloat(e.target.value))}
                      className="mt-2 bg-white/5 border-white/20 text-white"
                      data-testid="paper-balance-input"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Strategy Settings */}
            <Card className="bg-white/10 border-white/20 backdrop-blur-sm" data-testid="strategy-card">
              <CardHeader>
                <CardTitle className="text-white">Strategy Parameters</CardTitle>
                <CardDescription className="text-blue-200">
                  {availableStrategies.find(s => s.name === botSettings.strategy)?.description || 'Configure your strategy'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Symbol Selection */}
                <div>
                  <Label className="text-blue-100 font-semibold">Trading Pair</Label>
                  <Select 
                    value={customSymbol ? 'custom' : botSettings.symbol} 
                    onValueChange={handleSymbolChange}
                  >
                    <SelectTrigger className="mt-2 bg-white/5 border-white/20 text-white" data-testid="symbol-select">
                      <SelectValue placeholder="Select trading pair" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-white/20">
                      {getExchangeSymbols().map(symbol => (
                        <SelectItem key={symbol.value} value={symbol.value} className="text-white hover:bg-white/10">
                          {symbol.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {customSymbol && (
                    <Input
                      value={botSettings.symbol}
                      onChange={(e) => handleBotSettingChange('symbol', e.target.value)}
                      placeholder={`e.g., ${availableExchanges.find(e => e.id === botSettings.exchange)?.symbol_format || 'BTCUSD'}`}
                      className="mt-2 bg-white/5 border-white/20 text-white"
                      data-testid="custom-symbol-input"
                    />
                  )}
                </div>

                {/* Dynamic Strategy Parameters */}
                <StrategyParameters 
                  strategy={botSettings.strategy}
                  strategies={availableStrategies}
                  settings={botSettings}
                  onChange={handleBotSettingChange}
                />
              </CardContent>
            </Card>

            {/* Risk Management */}
            <Card className="bg-white/10 border-white/20 backdrop-blur-sm" data-testid="risk-management-card">
              <CardHeader>
                <CardTitle className="text-white">Risk Management</CardTitle>
                <CardDescription className="text-blue-200">Safety features to protect your capital</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-blue-100">Position Size (USD)</Label>
                    <Input
                      type="number"
                      value={botSettings.position_size}
                      onChange={(e) => handleBotSettingChange('position_size', parseFloat(e.target.value))}
                      className="mt-2 bg-white/5 border-white/20 text-white"
                      data-testid="position-size-input"
                    />
                  </div>
                  <div>
                    <Label className="text-blue-100">Max Position Size (USD)</Label>
                    <Input
                      type="number"
                      value={botSettings.max_position_size}
                      onChange={(e) => handleBotSettingChange('max_position_size', parseFloat(e.target.value))}
                      className="mt-2 bg-white/5 border-white/20 text-white"
                      data-testid="max-position-input"
                    />
                  </div>
                  <div>
                    <Label className="text-blue-100">Stop Loss (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={botSettings.stop_loss_pct}
                      onChange={(e) => handleBotSettingChange('stop_loss_pct', parseFloat(e.target.value))}
                      className="mt-2 bg-white/5 border-white/20 text-white"
                      data-testid="stop-loss-input"
                    />
                  </div>
                  <div>
                    <Label className="text-blue-100">Take Profit (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={botSettings.take_profit_pct}
                      onChange={(e) => handleBotSettingChange('take_profit_pct', parseFloat(e.target.value))}
                      className="mt-2 bg-white/5 border-white/20 text-white"
                      data-testid="take-profit-input"
                    />
                  </div>
                  <div>
                    <Label className="text-blue-100">Daily Loss Limit (USD)</Label>
                    <Input
                      type="number"
                      value={botSettings.daily_loss_limit}
                      onChange={(e) => handleBotSettingChange('daily_loss_limit', parseFloat(e.target.value))}
                      className="mt-2 bg-white/5 border-white/20 text-white"
                      data-testid="daily-loss-input"
                    />
                  </div>
                  <div>
                    <Label className="text-blue-100">Min Profit to Close (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={botSettings.min_profit_to_close_pct}
                      onChange={(e) => handleBotSettingChange('min_profit_to_close_pct', parseFloat(e.target.value))}
                      className="mt-2 bg-white/5 border-white/20 text-white"
                      data-testid="min-profit-input"
                    />
                    <p className="text-xs text-blue-300 mt-1">Will not close on opposite signal unless profit meets threshold</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Save Button */}
            <div className="flex justify-between items-center">
              <Link to="/">
                <Button
                  variant="outline"
                  className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                  data-testid="back-to-dashboard-btn"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Dashboard
                </Button>
              </Link>
              <Button
                onClick={handleSaveBotSettings}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700 text-white px-8"
                data-testid="save-bot-settings-btn"
              >
                <Save className="mr-2 h-4 w-4" />
                {loading ? 'Saving...' : 'Save Bot Settings'}
              </Button>
            </div>
          </TabsContent>

          {/* Exchange APIs Tab */}
          <TabsContent value="exchanges" className="space-y-6">
            {/* Add New Exchange */}
            <Card className="bg-white/10 border-white/20 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-white">Connect Exchange</CardTitle>
                <CardDescription className="text-blue-200">
                  Add your API keys to enable trading on an exchange
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-blue-100">Exchange</Label>
                    <Select value={selectedExchange} onValueChange={setSelectedExchange}>
                      <SelectTrigger className="mt-2 bg-white/5 border-white/20 text-white">
                        <SelectValue placeholder="Select exchange" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-white/20">
                        {EXCHANGES.filter(e => e.status === 'available').map(ex => (
                          <SelectItem key={ex.id} value={ex.id} className="text-white">
                            {ex.logo} {ex.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-blue-100">
                      {selectedExchange === 'revolutx' ? 'API Key (64-char string from Revolut X)' : 'API Key'}
                    </Label>
                    <Input
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder={selectedExchange === 'revolutx' ? "e.g., M1VKFtwB0M9C9QJO7goPlwrOytrJsSNE..." : "Enter API key"}
                      className="mt-2 bg-white/5 border-white/20 text-white"
                    />
                    {selectedExchange === 'revolutx' && (
                      <p className="text-xs text-blue-300 mt-1">
                        Get this from <a href="https://exchange.revolut.com/" target="_blank" rel="noopener noreferrer" className="underline">Revolut X</a> → Profile → API Keys (after uploading your public key)
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-blue-100">
                      {selectedExchange === 'revolutx' ? 'Private Key (Ed25519 PEM format)' : 'API Secret'}
                    </Label>
                    <Input
                      type="password"
                      value={apiSecret}
                      onChange={(e) => setApiSecret(e.target.value)}
                      placeholder={selectedExchange === 'revolutx' ? "-----BEGIN PRIVATE KEY-----..." : "Enter API secret"}
                      className="mt-2 bg-white/5 border-white/20 text-white"
                    />
                    {selectedExchange === 'revolutx' && (
                      <div className="mt-3 space-y-3">
                        <Button 
                          type="button"
                          variant="outline" 
                          size="sm"
                          onClick={() => setShowKeyGenerator(!showKeyGenerator)}
                          className="bg-purple-500/20 border-purple-400/30 text-purple-200 hover:bg-purple-500/30"
                        >
                          <Key className="mr-2 h-4 w-4" />
                          {showKeyGenerator ? 'Hide Key Generator' : 'Generate Ed25519 Keys'}
                        </Button>
                        
                        {showKeyGenerator && (
                          <div className="p-4 bg-slate-800/50 rounded-lg border border-purple-500/20 space-y-4">
                            <div className="flex items-start gap-2">
                              <Lock className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                              <p className="text-xs text-green-300">
                                <strong>Privacy Notice:</strong> Keys are generated entirely in your browser. 
                                No keys are saved, transmitted, or stored anywhere. Only you have access to them.
                              </p>
                            </div>
                            
                            <Button 
                              type="button"
                              onClick={generateEd25519KeyPair}
                              disabled={generatingKeys}
                              className="w-full bg-purple-600 hover:bg-purple-700"
                            >
                              {generatingKeys ? (
                                <>
                                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                                  Generating...
                                </>
                              ) : (
                                <>
                                  <RefreshCw className="mr-2 h-4 w-4" />
                                  Generate New Key Pair
                                </>
                              )}
                            </Button>
                            
                            {generatedKeys && (
                              <div className="space-y-4">
                                {/* Public Key */}
                                <div>
                                  <div className="flex items-center justify-between mb-1">
                                    <Label className="text-blue-100 text-sm">Public Key (paste in Revolut X)</Label>
                                    <Button 
                                      type="button"
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => copyToClipboard(generatedKeys.publicKey, 'Public key')}
                                      className="h-7 text-xs text-blue-300 hover:text-white"
                                    >
                                      <Copy className="mr-1 h-3 w-3" />
                                      Copy
                                    </Button>
                                  </div>
                                  <textarea 
                                    readOnly 
                                    value={generatedKeys.publicKey}
                                    className="w-full h-24 p-2 text-xs font-mono bg-slate-900/50 border border-white/10 rounded text-green-300 resize-none"
                                  />
                                </div>
                                
                                {/* Private Key */}
                                <div>
                                  <div className="flex items-center justify-between mb-1">
                                    <Label className="text-blue-100 text-sm">Private Key (keep secret!)</Label>
                                    <div className="flex gap-1">
                                      <Button 
                                        type="button"
                                        variant="ghost" 
                                        size="sm"
                                        onClick={() => copyToClipboard(generatedKeys.privateKey, 'Private key')}
                                        className="h-7 text-xs text-blue-300 hover:text-white"
                                      >
                                        <Copy className="mr-1 h-3 w-3" />
                                        Copy
                                      </Button>
                                      <Button 
                                        type="button"
                                        variant="ghost" 
                                        size="sm"
                                        onClick={useGeneratedPrivateKey}
                                        className="h-7 text-xs text-green-300 hover:text-white"
                                      >
                                        <CheckCircle className="mr-1 h-3 w-3" />
                                        Use This Key
                                      </Button>
                                    </div>
                                  </div>
                                  <textarea 
                                    readOnly 
                                    value={generatedKeys.privateKey}
                                    className="w-full h-24 p-2 text-xs font-mono bg-slate-900/50 border border-white/10 rounded text-yellow-300 resize-none"
                                  />
                                </div>
                                
                                <Alert className="bg-blue-500/10 border-blue-500/30">
                                  <AlertCircle className="h-4 w-4 text-blue-400" />
                                  <AlertDescription className="text-blue-200 text-xs space-y-2">
                                    <p><strong>Setup Instructions:</strong></p>
                                    <ol className="list-decimal list-inside space-y-1 ml-2">
                                      <li>Copy the <strong>Public Key</strong> above</li>
                                      <li>Go to <a href="https://exchange.revolut.com/" target="_blank" rel="noopener noreferrer" className="text-blue-300 underline">Revolut X</a> → Profile → API Keys</li>
                                      <li>Create new API key and paste your <strong>Public Key</strong></li>
                                      <li><strong className="text-yellow-300">Important:</strong> When asked about IP restrictions, either disable them or leave the IP whitelist empty to allow access from any IP</li>
                                      <li>Revolut X will give you a <strong>64-character API Key</strong> - paste it in the "API Key" field above</li>
                                      <li>Click "Use This Key" to fill the Private Key field, then Save</li>
                                    </ol>
                                  </AlertDescription>
                                </Alert>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <Button onClick={handleSaveExchange} disabled={loading} className="bg-green-600 hover:bg-green-700">
                  <Save className="mr-2 h-4 w-4" />
                  Save Configuration
                </Button>
              </CardContent>
            </Card>

            {/* Connected Exchanges */}
            <Card className="bg-white/10 border-white/20 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-white">Your Exchanges</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {EXCHANGES.map(exchange => {
                    const config = getConfiguredExchange(exchange.id);
                    const isConfigured = !!config;
                    const isAvailable = exchange.status === 'available';

                    return (
                      <div
                        key={exchange.id}
                        className={`p-4 rounded-lg border ${
                          isConfigured 
                            ? 'bg-green-500/10 border-green-500/30' 
                            : 'bg-white/5 border-white/10'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{exchange.logo}</span>
                            <div>
                              <h3 className="text-white font-semibold">{exchange.name}</h3>
                              {isConfigured ? (
                                <p className="text-green-400 text-sm flex items-center">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Connected
                                </p>
                              ) : isAvailable ? (
                                <p className="text-gray-400 text-sm">Not configured</p>
                              ) : exchange.status === 'geo_restricted' ? (
                                <Badge variant="secondary" className="text-xs bg-orange-500/20 text-orange-300">Region Restricted</Badge>
                              ) : (
                                <Badge variant="secondary" className="text-xs">Coming Soon</Badge>
                              )}
                            </div>
                          </div>
                          {isConfigured && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteExchange(exchange.id)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <Card className="bg-white/10 border-white/20 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-white">Transaction History</CardTitle>
              </CardHeader>
              <CardContent>
                {transactions.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No transactions yet</p>
                ) : (
                  <div className="space-y-3">
                    {transactions.map((tx) => (
                      <div key={tx.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                        <div>
                          <p className="text-white font-medium capitalize">
                            {tx.type.replace('_', ' ')}
                          </p>
                          <p className="text-gray-400 text-sm">{tx.description || formatDate(tx.created_at)}</p>
                        </div>
                        <div className="text-right">
                          <p className={`font-semibold ${getTransactionColor(tx.type)}`}>
                            {tx.amount_credits > 0 ? '+' : ''}{tx.amount_credits} credits
                          </p>
                          <p className="text-gray-400 text-sm">
                            Balance: {tx.balance_after}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card className="bg-white/10 border-white/20 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-white">Account Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label className="text-blue-100">Email</Label>
                  <Input
                    value={user?.email || ''}
                    disabled
                    className="mt-2 bg-white/5 border-white/20 text-white"
                  />
                </div>
                <div>
                  <Label className="text-blue-100">Name</Label>
                  <Input
                    value={user?.name || ''}
                    disabled
                    className="mt-2 bg-white/5 border-white/20 text-white"
                  />
                </div>
                <div>
                  <Label className="text-blue-100">Account Type</Label>
                  <div className="mt-2">
                    <Badge className={user?.role === 'admin' ? 'bg-purple-500' : 'bg-blue-500'}>
                      {user?.role === 'admin' ? 'Administrator' : 'Standard User'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AccountPage;
